<script setup lang="ts">
import { useTemplateRef, ref, inject } from 'vue'
import { GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import BaseModal from '@/components/base/BaseModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useHelpers } from '@/composables/useHelpers'

const socket = useSocket()
const { handleCustomError } = useHelpers()

const showLoader = inject('showLoader') as () => void
const hideLoader = inject('hideLoader') as () => void

const modalRef = useTemplateRef('modalRef')

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

defineExpose({
  open,
  close
})

const code = ref('')

const joinGame = async () => {
  if (code.value.length < 6) return
  try {
    showLoader()
    await socket.sendEventAsync(GameEvent.PlayPrivate, { code: code.value })
    close()
  } catch (error: any) {
    // Show specific message for already started games
    const errorMessage = error?.message || error || ''

    if (errorMessage.toLowerCase().includes('started') || errorMessage.toLowerCase().includes('in progress') || errorMessage.toLowerCase().includes('already begun')) {
      handleCustomError('This game has already started, you can start another to play.')
    } else if (errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('does not exist')) {
      handleCustomError('Game not found. Please check the code and try again.')
    } else {
      handleCustomError(error || 'Failed to join game. Please try again.')
    }
  } finally {
    hideLoader()
  }
}
</script>

<template>
  <BaseModal ref="modalRef" class="lg:px-16">
    <div class="text-center">
      <div class="mb-4 lg:mb-7 flex flex-col gap-y-1 lg:gap-y-2 items-center">
        <h2 class="text-xl lg:text-2xl font-bold text-white">Join a game</h2>
        <p class="text-xs font-medium">Enter game code to join</p>
      </div>
      <div class="grid grid-cols-1 gap-y-5 items-center">
        <div class="input-group">
          <input v-model="code" type="text" class="input-item w-full" />
        </div>

        <div class="*:!w-full">
          <BaseButton background-color="var(--color-cw-purple)" border-color="#1B0623" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" :disabled="code.length < 6" @click="joinGame">
            <p class="w-full !text-sm md:!text-base xl:!text-xl">Join game</p>
          </BaseButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
